import React from 'react'
import { ItemRenderProps } from '@components/UniversalAPISelector'
import { useBusinessContext } from '@root/context'
import { fetchAccountingRules, IAccountingRule } from '@root/query'
import { motion } from 'framer-motion'
import { InputProps } from '@components'
import styled from 'styled-components'
import { FaBook, FaCheck } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import UniversalAPISelector from '@root/components/UniversalAPISelector/UniversalAPISelector'

interface Props extends InputProps {
  onChange: (ruleId: number) => void
}

const PAGE_SIZE = 200

export const RuleSelector: React.FC<Props> = ({ ...rest }) => {
  const { slug } = useBusinessContext()
  const { t } = useTranslation()

  const dataToShow = (item: IAccountingRule): string => item.name

  const renderRow = (
    item: IAccountingRule,
    { isFocused, isSelected, onKeyDown, handleOnItemClick, style }: ItemRenderProps<IAccountingRule>
  ) => {
    return (
      <ItemWrapper
        className={isFocused && 'keyboard-focused'}
        onKeyDown={onKeyDown}
        onClick={() => handleOnItemClick(item)}
        style={style}
        key={item.id}
      >
        <Item key={`rule-${item.id}`} title={item.name}>
          <ItemColumn>{item.name}</ItemColumn>
          <RightItemsWrapper>
            {isSelected && (
              <ItemColumn>
                <CheckMarkWrapper>
                  <FaCheck />
                </CheckMarkWrapper>
              </ItemColumn>
            )}
          </RightItemsWrapper>
        </Item>
      </ItemWrapper>
    )
  }

  return (
    <UniversalAPISelector
      {...rest}
      getItemQueryKeyFn={itemId => [slug, 'automationRules', itemId]}
      getItem={async id => {
        const data = await fetchAccountingRules({ slug }, { page_size: PAGE_SIZE })
        return data.results.find(r => r.id === id)
      }}
      getItemsQueryKeyFn={params => [slug, 'automationRules', 'selector', params]}
      getItems={async params => {
        return fetchAccountingRules({ slug }, { ...params, page_size: PAGE_SIZE })
      }}
      renderRow={renderRow}
      noDataMessage={t('document.filter.rule.placeholder')}
      dropdownHeaderIcon={<FaBook />}
      dropDownHeader={t('document.filter.rule.label')}
      placeholder={t('document.filter.rule.placeholder')}
      formId="rule-selector"
      getDataToShow={dataToShow}
    />
  )
}

const Item = styled(motion.div).attrs({
  variants: {
    hidden: { y: 5, opacity: 0 },
    show: { y: 0, opacity: 1 }
  }
})`
  display: flex;
  width: 100%;
  padding: 0.4rem 0.6rem;
  cursor: pointer;
  height: 35px;
`

const ItemWrapper = styled.div`
  display: flex;
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  &.keyboard-focused,
  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`

const RightItemsWrapper = styled.div`
  margin-left: auto;
  display: flex;
`

const ItemColumn = styled.div`
  align-self: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CheckMarkWrapper = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  margin: ${({ theme }) => theme.spacing.sm}rem;
  svg {
    fill: ${({ theme }) => theme.colors.success};
  }
`
